import { usePortal } from "../HubPortalContext";
import { ROLE_CONFIGS } from "@/lib/hubTypes";
import HUB_TYPES from "@/lib/hubTypes";
import {
  ArrowRight, ArrowLeft, Loader2, X, PlusCircle,
  Users, Bookmark
} from "lucide-react";

const ROLE_CONFIG = {
  planner: { icon: require("lucide-react").LayoutDashboard, label: "Planner", color: "bg-lime-500", textColor: "text-slate-950" },
  driver: { icon: require("lucide-react").Truck, label: "Driver", color: "bg-blue-500", textColor: "text-white" },
  machinist: { icon: require("lucide-react").Wrench, label: "Machinist", color: "bg-amber-500", textColor: "text-slate-950" },
  service: { icon: require("lucide-react").Droplets, label: "Service", color: "bg-violet-500", textColor: "text-white" },
  transport: { icon: require("lucide-react").Navigation, label: "Transport", color: "bg-teal-500", textColor: "text-white" },
  viewer: { icon: require("lucide-react").Eye, label: "Viewer", color: "bg-slate-400", textColor: "text-white" },
};

export default function HubCreateWizard() {
  const ctx = usePortal();
  const {
    t, wizardStep, setWizardStep, selectedAssetType, setSelectedAssetType,
    customItems, setCustomItems, customUnits, setCustomUnits,
    itemBEnabled, setItemBEnabled, itemCEnabled, setItemCEnabled,
    customActions, setCustomActions, selectedRoleConfig, setSelectedRoleConfig,
    hubName, setHubName, hubDesc, setHubDesc,
    saveAsTemplate, setSaveAsTemplate, templateName, setTemplateName,
    creating, handleCreateHub, templates, handleDeleteTemplate, applyTemplate,
    setView,
  } = ctx;

  return (
    <div className="w-full max-w-lg" data-testid="create-hub-view">
      {/* Step 1: Choose Asset Type */}
      {wizardStep === 1 && (
        <div className="animate-fadeInUp">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-black tracking-tighter uppercase text-slate-900 mb-2">{t("new_hub_title")}</h2>
            <p className="text-sm text-slate-400 font-medium">{t("new_hub_subtitle")}</p>
          </div>

          {templates.length > 0 && (
            <div className="mb-6">
              <p className="text-[10px] font-bold text-amber-500 uppercase tracking-widest mb-2">{t("saved_templates")}</p>
              <div className="grid gap-2">
                {templates.map((tpl) => {
                  const tplCfg = HUB_TYPES[tpl.asset_type] || HUB_TYPES.refueling;
                  const TIcon = tplCfg.icon;
                  return (
                    <div key={tpl.id} className="flex items-center gap-2">
                      <button onClick={() => applyTemplate(tpl)}
                        className="flex-1 p-3 bg-amber-50 border border-amber-200 hover:border-amber-400 rounded-xl text-left flex items-center gap-3 transition-all"
                        data-testid={`apply-template-${tpl.id}`}>
                        <TIcon className="w-5 h-5 text-amber-500" />
                        <div>
                          <span className="text-sm font-bold text-slate-800">{tpl.name}</span>
                          <p className="text-[10px] text-amber-500 font-bold uppercase">{tplCfg.label}</p>
                        </div>
                      </button>
                      <button onClick={() => handleDeleteTemplate(tpl.id)}
                        className="p-2 text-slate-300 hover:text-red-500 transition-opacity"
                        data-testid={`delete-template-${tpl.id}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">{t("choose_type_label")}</p>
          <div className="grid gap-2 mb-6">
            {Object.entries(HUB_TYPES).map(([key, cfg]) => {
              const TypeIcon = cfg.icon;
              return (
                <button
                  key={key}
                  data-testid={`asset-type-${key}`}
                  onClick={() => { setSelectedAssetType(key); setWizardStep(2); if (cfg.is_task_based && customActions.length === 0) setCustomActions([""]); }}
                  className={`p-4 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${selectedAssetType === key ? "border-lime-400 bg-lime-50/50" : "border-slate-200 bg-white hover:border-slate-300"}`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${selectedAssetType === key ? "bg-lime-500 text-slate-950" : "bg-slate-100 text-slate-400"}`}>
                    <TypeIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-bold text-slate-900">{cfg.label}</span>
                    <p className="text-xs text-slate-400 mt-0.5">{cfg.description}</p>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex gap-3">
            <button onClick={() => { setView("list"); setWizardStep(1); setSelectedAssetType(""); }}
              className="flex-1 py-4 bg-slate-100 hover:bg-slate-200 text-slate-400 rounded-xl text-sm font-bold">
              {t("cancel")}
            </button>
            <button data-testid="wizard-step1-next"
              disabled={!selectedAssetType}
              onClick={() => setWizardStep(2)}
              className="flex-[2] py-4 bg-lime-500 hover:bg-lime-400 disabled:bg-slate-100 disabled:text-slate-400 text-slate-950 rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2 shadow-md">
              {t("next_btn")} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Customize Assets + Role Config */}
      {wizardStep === 2 && (() => {
        const cfg = HUB_TYPES[selectedAssetType] || HUB_TYPES.refueling;
        const TypeIcon = cfg.icon;
        return (
          <div className="animate-fadeInUp">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-lime-50 border border-lime-300 rounded-xl flex items-center justify-center mx-auto mb-3">
                <TypeIcon className="w-6 h-6 text-lime-500" />
              </div>
              <h2 className="text-2xl font-black tracking-tighter uppercase text-slate-900 mb-1">{t("customize_hub")}</h2>
              <p className="text-xs text-slate-400 font-medium">{cfg.label} — {t("define_items_crew")}</p>
            </div>

            {!cfg.is_task_based && (
              <div className="space-y-3 mb-6">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t("asset_items")}</p>
                <div className="flex gap-2">
                  <input data-testid="item-a-label" value={customItems.a} onChange={e => setCustomItems(p => ({...p, a: e.target.value}))}
                    placeholder={cfg.item_a.label} className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 placeholder:text-slate-300 focus:border-lime-500 outline-none" />
                  <input data-testid="item-a-unit" value={customUnits.a} onChange={e => setCustomUnits(p => ({...p, a: e.target.value}))}
                    placeholder={cfg.item_a.unit} className="w-20 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 text-center placeholder:text-slate-300 focus:border-lime-500 outline-none" />
                </div>
                {itemBEnabled ? (
                  <div className="flex gap-2">
                    <input data-testid="item-b-label" value={customItems.b} onChange={e => setCustomItems(p => ({...p, b: e.target.value}))}
                      placeholder={cfg.item_b.label || "Secondary item"} className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 placeholder:text-slate-300 focus:border-lime-500 outline-none" />
                    <input data-testid="item-b-unit" value={customUnits.b} onChange={e => setCustomUnits(p => ({...p, b: e.target.value}))}
                      placeholder={cfg.item_b.unit || "unit"} className="w-20 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 text-center placeholder:text-slate-300 focus:border-lime-500 outline-none" />
                    <button onClick={() => setItemBEnabled(false)} className="p-3 text-slate-300 hover:text-red-400"><X className="w-4 h-4" /></button>
                  </div>
                ) : !itemBEnabled && !itemCEnabled && (
                  <button onClick={() => setItemBEnabled(true)} className="flex items-center gap-2 text-xs text-lime-600 font-bold hover:text-lime-500">
                    <PlusCircle className="w-4 h-4" /> {t("add_second_item")}
                  </button>
                )}
                {itemBEnabled && cfg.max_items >= 3 && (
                  itemCEnabled ? (
                    <div className="flex gap-2">
                      <input data-testid="item-c-label" value={customItems.c} onChange={e => setCustomItems(p => ({...p, c: e.target.value}))}
                        placeholder="Third item" className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 placeholder:text-slate-300 focus:border-lime-500 outline-none" />
                      <input data-testid="item-c-unit" value={customUnits.c} onChange={e => setCustomUnits(p => ({...p, c: e.target.value}))}
                        placeholder="unit" className="w-20 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 text-center placeholder:text-slate-300 focus:border-lime-500 outline-none" />
                      <button onClick={() => { setItemCEnabled(false); setCustomItems(p => ({...p, c: ""})); }} className="p-3 text-slate-300 hover:text-red-400"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <button onClick={() => setItemCEnabled(true)} className="flex items-center gap-2 text-xs text-lime-600 font-bold hover:text-lime-500">
                      <PlusCircle className="w-4 h-4" /> {t("add_third_item")}
                    </button>
                  )
                )}
              </div>
            )}

            {cfg.is_task_based && (
              <div className="space-y-3 mb-6">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t("define_actions")}</p>
                {customActions.map((action, i) => (
                  <div key={`action-${i}`} className="flex gap-2">
                    <input data-testid={`action-input-${i}`} value={action}
                      onChange={e => { const a = [...customActions]; a[i] = e.target.value; setCustomActions(a); }}
                      placeholder={["e.g. Cleaning", "e.g. Apply Emulsion Coating", "e.g. Water Plants", "e.g. Site Inspection", "e.g. Waste Removal"][i] || "New action..."}
                      className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 placeholder:text-slate-300 placeholder:font-normal focus:border-lime-500 outline-none" />
                    {customActions.length > 1 && (
                      <button onClick={() => setCustomActions(customActions.filter((_, j) => j !== i))} className="p-3 text-slate-300 hover:text-red-400"><X className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
                <button data-testid="add-action-btn" onClick={() => setCustomActions([...customActions, ""])}
                  className="flex items-center gap-2 text-xs text-lime-600 font-bold hover:text-lime-500 py-2">
                  <PlusCircle className="w-4 h-4" /> Add action
                </button>
              </div>
            )}

            <div className="mb-6">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">{t("crew_config_title") || "Crew Assembly"}</p>
              <div className="space-y-2">
                {ROLE_CONFIGS.map(rc => {
                  const isActive = selectedRoleConfig === rc.id;
                  const titleKey = rc.id === "full" ? "crew_cfg_full_title" : rc.id === "planner_driver" ? "crew_cfg_pd_title" : rc.id === "planner_machinist" ? "crew_cfg_pm_title" : "crew_cfg_solo_title";
                  const descKey = rc.id === "full" ? "crew_cfg_full_desc" : rc.id === "planner_driver" ? "crew_cfg_pd_desc" : rc.id === "planner_machinist" ? "crew_cfg_pm_desc" : "crew_cfg_solo_desc";
                  return (
                    <button key={rc.id} data-testid={`role-config-${rc.id}`} onClick={() => setSelectedRoleConfig(rc.id)}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${isActive ? "border-lime-400 bg-lime-50/50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-slate-900">{t(titleKey) || rc.label}</span>
                        <span className={`text-[9px] font-black px-2 py-0.5 rounded-full ${isActive ? "bg-lime-500 text-slate-950" : "bg-slate-100 text-slate-400"}`}>{rc.comboTag}</span>
                      </div>
                      <p className="text-xs text-slate-500 mb-3">{t(descKey) || rc.description}</p>
                      <div className="flex items-center gap-1.5">
                        {rc.youRoles.map(r => {
                          const roleC = ROLE_CONFIG[r];
                          const RIcon = roleC?.icon;
                          return (
                            <div key={r} className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[9px] font-bold ${isActive ? `${roleC?.color} ${roleC?.textColor}` : "bg-slate-100 text-slate-500"}`}>
                              {RIcon && <RIcon className="w-3 h-3" />} {t("role_" + r) || roleC?.label}
                              <span className="opacity-60">({t("crew_you_label") || "YOU"})</span>
                            </div>
                          );
                        })}
                        {rc.inviteRoles.map(r => {
                          const roleC = ROLE_CONFIG[r];
                          const RIcon = roleC?.icon;
                          return (
                            <div key={r} className={`flex items-center gap-1 px-2 py-1 rounded-lg border border-dashed text-[9px] font-bold ${isActive ? "border-lime-400 text-lime-700 bg-lime-50" : "border-slate-300 text-slate-400 bg-slate-50"}`}>
                              {RIcon && <RIcon className="w-3 h-3" />} {t("role_" + r) || roleC?.label}
                              <span className="opacity-60">({t("crew_invite_label") || "INVITE"})</span>
                            </div>
                          );
                        })}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-3">
              <button type="button" onClick={() => setWizardStep(1)} className="flex-1 py-4 bg-slate-100 hover:bg-slate-200 text-slate-400 rounded-xl text-sm font-bold flex items-center justify-center gap-1">
                <ArrowLeft className="w-4 h-4" /> {t("back")}
              </button>
              <button data-testid="wizard-step2-next" onClick={() => setWizardStep(3)}
                className="flex-[2] py-4 bg-lime-500 hover:bg-lime-400 text-slate-950 rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2 shadow-md">
                {t("next_btn")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })()}

      {/* Step 3: Name + Deploy */}
      {wizardStep === 3 && (() => {
        const cfg = HUB_TYPES[selectedAssetType] || HUB_TYPES.refueling;
        const TypeIcon = cfg.icon;
        const roleConf = ROLE_CONFIGS.find(r => r.id === selectedRoleConfig) || ROLE_CONFIGS[0];
        return (
          <div className="animate-fadeInUp">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-lime-50 border border-lime-300 rounded-xl flex items-center justify-center mx-auto mb-3">
                <TypeIcon className="w-6 h-6 text-lime-500" />
              </div>
              <h2 className="text-2xl font-black tracking-tighter uppercase text-slate-900 mb-1">{t("deploy_hub")}</h2>
              <p className="text-xs text-slate-400 font-medium">{cfg.label} &middot; {roleConf.label}</p>
            </div>

            <form onSubmit={handleCreateHub} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{t("hub_name_label")}</label>
                <input data-testid="hub-name-input" value={hubName} onChange={(e) => setHubName(e.target.value)} placeholder="e.g. Stadsbader Ops" autoFocus
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-bold placeholder:text-slate-400 focus:border-lime-500 focus:ring-1 focus:ring-lime-500/20 outline-none transition-all" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{t("description_label")}</label>
                <input data-testid="hub-desc-input" value={hubDesc} onChange={(e) => setHubDesc(e.target.value)} placeholder="Optional description..."
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-medium placeholder:text-slate-400 focus:border-lime-500 focus:ring-1 focus:ring-lime-500/20 outline-none transition-all" />
              </div>

              <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">{t("config_summary")}</p>
                {!cfg.is_task_based ? (
                  <>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="w-2 h-2 bg-lime-500 rounded-full" />
                      <span className="font-bold text-slate-800">{customItems.a || cfg.item_a.label}</span>
                      <span className="text-slate-400">({customUnits.a || cfg.item_a.unit})</span>
                    </div>
                    {itemBEnabled && (customItems.b || cfg.item_b.label) && (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="w-2 h-2 bg-blue-500 rounded-full" />
                        <span className="font-bold text-slate-800">{customItems.b || cfg.item_b.label}</span>
                        <span className="text-slate-400">({customUnits.b || cfg.item_b.unit})</span>
                      </div>
                    )}
                    {itemCEnabled && customItems.c && (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="w-2 h-2 bg-purple-500 rounded-full" />
                        <span className="font-bold text-slate-800">{customItems.c}</span>
                        <span className="text-slate-400">({customUnits.c})</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {customActions.filter(Boolean).map((a, i) => (
                      <span key={`summary-${i}`} className="text-[10px] font-bold bg-orange-50 text-orange-600 border border-orange-200 px-2 py-1 rounded-full">{a}</span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2 text-xs pt-2 border-t border-slate-200 mt-2">
                  <Users className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-slate-500">{roleConf.label} &middot; Min {roleConf.min_occupancy}</span>
                </div>
              </div>

              <div className="p-3 bg-amber-50/60 border border-amber-200 rounded-xl" data-testid="save-template-section">
                <label className="flex items-center gap-3 cursor-pointer" data-testid="save-template-toggle">
                  <input type="checkbox" checked={saveAsTemplate} onChange={e => setSaveAsTemplate(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-400" />
                  <div className="flex items-center gap-1.5">
                    <Bookmark className="w-3.5 h-3.5 text-amber-500" />
                    <span className="text-xs font-bold text-slate-700">{t("save_as_template")}</span>
                  </div>
                </label>
                {saveAsTemplate && (
                  <input data-testid="template-name-input" value={templateName} onChange={e => setTemplateName(e.target.value)}
                    placeholder="Template name, e.g. 'Cleaning Crew Setup'"
                    className="w-full mt-2 p-3 bg-white border border-amber-200 rounded-lg text-sm font-bold text-slate-900 placeholder:text-slate-400 focus:border-amber-400 outline-none" />
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setWizardStep(2)} className="flex-1 py-4 bg-slate-100 hover:bg-slate-200 text-slate-400 rounded-xl text-sm font-bold flex items-center justify-center gap-1">
                  <ArrowLeft className="w-4 h-4" /> {t("back")}
                </button>
                <button data-testid="submit-create-hub-btn" type="submit" disabled={creating || !hubName.trim()}
                  className="flex-[2] py-4 bg-lime-500 hover:bg-lime-400 disabled:bg-slate-100 disabled:text-slate-400 text-slate-950 rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2 shadow-md">
                  {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t("deploy_hub")} <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            </form>

            <div className="mt-5 p-3 bg-slate-50/80 border border-slate-200 rounded-xl">
              <p className="text-xs text-slate-400 font-medium leading-relaxed">
                {t("assigned_as_role")} <span className="text-lime-600 font-bold">{t("role_planner")}</span>.
                {roleConf.youRoles?.length > 1 && <> {t("you_also_serve")} <span className="font-bold text-slate-600">{roleConf.youRoles.filter(r => r !== "planner").map(r => t("role_" + r) || ROLE_CONFIG[r]?.label || r).join(", ")}</span>.</>}
                {roleConf.inviteRoles?.length > 0 && <> {t("invite_crew_for")} <span className="font-bold text-slate-600">{roleConf.inviteRoles.map(r => t("role_" + r) || ROLE_CONFIG[r]?.label || r).join(", ")}</span>.</>}
                {roleConf.min_occupancy === 1 && <> {t("solo_manage")}</>}
              </p>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
